import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, gbp, Loading } from "@/components/ui-bits";
import TeamInbox from "@/components/TeamInbox";
import { ReferencingPanel } from "@/pages/Referencing";
import { toast } from "sonner";
import { Plus, X, Loader2, Trash2, User, Calendar, KeyRound, Settings2, Globe, Check, ExternalLink, ClipboardCheck, Megaphone, Radio } from "lucide-react";
import { usePortalReadiness, PortalBadge, MarketingModal } from "@/components/ListingMarketing";

const empty = { property_ref: "", address: "", rent_pcm: "", applicant_name: "", agent: "", target_date: "", notes: "", stage: "Awaiting Marketing" };
const PORTALS = [
  { key: "Rightmove", desc: "UK's largest property portal", color: "bg-[#00DEB6]/15 text-[#0a7a67] border-[#00DEB6]/30" },
  { key: "Zoopla", desc: "Major listings & valuations portal", color: "bg-[#8046F1]/10 text-[#5b2bb0] border-[#8046F1]/30" },
  { key: "OpenRent", desc: "Direct-to-tenant lettings portal", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "SpareRoom", desc: "Room shares & HMO rooms", color: "bg-rose-50 text-rose-700 border-rose-200" },
];

export default function Lettings() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  const [tab, setTab] = useState("pipeline");
  const [refOpenCase, setRefOpenCase] = useState(null);
  const [stages, setStages] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [mgOpen, setMgOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [portalFor, setPortalFor] = useState(null);
  const [portalSel, setPortalSel] = useState([]);
  const [portalBusy, setPortalBusy] = useState(false);
  const [marketingFor, setMarketingFor] = useState(null);
  const [zooplaBusy, setZooplaBusy] = useState(null);

  const publishZoopla = async (l) => {
    setZooplaBusy(l.letting_id);
    try {
      const { data } = await api.post(`/zoopla/listings/${l.letting_id}/publish`);
      if (data.result?.ok) {
        toast.success("Published to Zoopla");
        setItems((prev) => prev.map((x) => x.letting_id === l.letting_id ? { ...x, zoopla_status: "published" } : x));
      } else {
        toast.error(`Zoopla error ${data.result?.status_code}: ${JSON.stringify(data.result?.body)?.slice(0, 140)}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish to Zoopla"); }
    finally { setZooplaBusy(null); }
  };

  const removeZoopla = async (l) => {
    if (!window.confirm("Remove this listing from Zoopla?")) return;
    setZooplaBusy(l.letting_id);
    try {
      const { data } = await api.post(`/zoopla/listings/${l.letting_id}/remove`);
      if (data.result?.ok) {
        toast.success("Removed from Zoopla");
        setItems((prev) => prev.map((x) => x.letting_id === l.letting_id ? { ...x, zoopla_status: "removed" } : x));
      } else { toast.error(`Zoopla error ${data.result?.status_code}`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove from Zoopla"); }
    finally { setZooplaBusy(null); }
  };
  const { map: readyMap, refresh: refreshReadiness } = usePortalReadiness("let", items);

  const loadStages = () => api.get("/letting-stages").then(({ data }) => { setStages(data.stages); setDefaults(data.defaults); });
  const load = () => api.get("/lettings").then(({ data }) => setItems(data));
  const [props, setProps] = useState([]);
  const loadProps = () => api.get("/properties").then(({ data }) => setProps(data)).catch(() => {});
  useEffect(() => { load(); loadStages(); loadProps(); }, []);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) { setTab("referencing"); setRefOpenCase(ref); }
  }, [searchParams]);

  const pickProperty = (propertyId) => {
    const pr = props.find((x) => x.property_id === propertyId);
    if (!pr) { setForm((f) => ({ ...f, property_id: "", property_ref: "", address: "" })); return; }
    setForm((f) => ({ ...f, property_id: pr.property_id, property_ref: pr.reference,
      address: [pr.address, pr.city, pr.postcode].filter(Boolean).join(", "),
      rent_pcm: f.rent_pcm || (pr.rent_pcm ? String(pr.rent_pcm) : "") }));
  };

  const PORTAL_URLS = {
    Rightmove: (q) => `https://www.rightmove.co.uk/property-to-rent/find.html?searchLocation=${encodeURIComponent(q)}`,
    Zoopla: (q) => `https://www.zoopla.co.uk/to-rent/property/?q=${encodeURIComponent(q)}`,
    OpenRent: (q) => `https://www.openrent.co.uk/properties-to-rent/?term=${encodeURIComponent(q)}`,
    SpareRoom: (q) => `https://www.spareroom.co.uk/flatshare/?search=${encodeURIComponent(q)}`,
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/lettings", { ...form, rent_pcm: parseFloat(form.rent_pcm) || 0, target_date: form.target_date || null });
      toast.success("New letting opened");
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Could not open letting"); }
    finally { setBusy(false); }
  };

  const changeStage = async (l, stage) => {
    setItems((prev) => prev.map((x) => x.letting_id === l.letting_id ? { ...x, stage } : x));
    try { await api.patch(`/lettings/${l.letting_id}`, { stage }); toast.success(`Moved to “${stage}”`); }
    catch { toast.error("Could not update status"); load(); }
  };

  const startReferencing = async (l) => {
    try {
      const { data } = await api.post(`/lettings/${l.letting_id}/referencing`);
      setItems((prev) => prev.map((x) => x.letting_id === l.letting_id ? { ...x, stage: "Referencing" } : x));
      toast.success("Referencing case opened");
      setRefOpenCase(data.case_id);
      setTab("referencing");
    } catch { toast.error("Could not start referencing"); }
  };

  const remove = async (l) => {
    await api.delete(`/lettings/${l.letting_id}`);
    setItems((prev) => prev.filter((x) => x.letting_id !== l.letting_id));
  };

  const addStatus = async () => {
    const name = newStatus.trim();
    if (!name) return;
    try { await api.post("/letting-stages", { name }); setNewStatus(""); await loadStages(); toast.success("Status added"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add status"); }
  };

  const removeStatus = async (name) => {
    try { await api.delete(`/letting-stages?name=${encodeURIComponent(name)}`); await loadStages(); toast.success("Status removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove status"); }
  };

  const countFor = (stage) => (items || []).filter((l) => l.stage === stage).length;
  const filtered = !items ? [] : (filter === "All" ? items : items.filter((l) => l.stage === filter));

  const openPortals = (l) => { setPortalFor(l); setPortalSel(l.portals || []); };
  const togglePortal = (k) => setPortalSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);
  const savePortals = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post(`/lettings/${portalFor.letting_id}/portals`, { portals: portalSel });
      setItems((prev) => prev.map((x) => x.letting_id === portalFor.letting_id ? { ...x, portals: data.portals, stage: data.stage || x.stage } : x));
      toast.success(portalSel.length ? `Published to ${portalSel.join(", ")}` : "Removed from all portals");
      setPortalFor(null);
    } catch { toast.error("Could not publish to portals"); }
    finally { setPortalBusy(false); }
  };

  return (
    <Layout title="Lettings" subtitle="Letting Centre">
      <div className="mb-8">
        <TeamInbox department="Letting" title="Letting — Landlord Messages" />
      </div>

      <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1 mb-6" data-testid="lettings-tabs">
        <button data-testid="tab-pipeline" onClick={() => setTab("pipeline")}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${tab === "pipeline" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          Lettings pipeline
        </button>
        <button data-testid="tab-referencing" onClick={() => { setTab("referencing"); setRefOpenCase(null); }}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center gap-1.5 ${tab === "referencing" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          <ClipboardCheck size={15} /> Referencing
        </button>
      </div>

      {tab === "referencing" && <ReferencingPanel openCaseId={refOpenCase} />}

      {tab === "pipeline" && (<>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Lettings pipeline</h2>
          <p className="text-sm text-slate-500">Track each let across every stage — click a status to filter.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="manage-statuses-button" onClick={() => setMgOpen(true)}
            className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-2.5 text-sm font-medium flex items-center gap-2">
            <Settings2 size={15} /> Statuses
          </button>
          <button data-testid="new-letting-button" onClick={() => setOpen(true)}
            className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors">
            <Plus size={16} /> New Letting
          </button>
        </div>
      </div>

      {/* Pill filter bar */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6" data-testid="lettings-filter-bar">
        <Pill active={filter === "All"} onClick={() => setFilter("All")} label="All" count={(items || []).length} testid="letting-pill-all" />
        {stages.map((s) => (
          <Pill key={s} active={filter === s} onClick={() => setFilter(s)} label={s} count={countFor(s)} testid={`letting-pill-${s}`} />
        ))}
      </div>

      {!items ? <Loading /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lettings-list">
          {filtered.length === 0 && (
            <Card className="p-10 text-center text-slate-400 text-sm md:col-span-2 lg:col-span-3">
              {filter === "All" ? "No lettings yet — open your first one." : `No lettings at “${filter}”.`}
            </Card>
          )}
          {filtered.map((l) => (
            <Card key={l.letting_id} className="p-4 fade-up" data-testid={`letting-${l.letting_id}`}>
              <div className="flex items-start justify-between gap-1 mb-1">
                <p className="text-xs font-mono text-slate-400">{l.reference}</p>
                <button data-testid={`letting-delete-${l.letting_id}`} onClick={() => remove(l)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
              <p className="text-sm font-semibold text-slate-900 leading-snug truncate">{l.address || l.property_ref || "Letting"}</p>
              {l.rent_pcm > 0 && <p className="text-xs font-medium text-slate-700 mt-1">{gbp(l.rent_pcm)}/mo</p>}
              {l.applicant_name && <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><User size={11} /> {l.applicant_name}</p>}
              {l.target_date && <p className="text-xs text-slate-400 flex items-center gap-1"><Calendar size={11} /> {new Date(l.target_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</p>}
              <div className="mt-2"><PortalBadge readiness={readyMap[l.letting_id]} testid={`portal-badge-${l.letting_id}`} /></div>
              {l.portals?.length > 0 && (
                <div className="mt-2.5" data-testid={`letting-portals-${l.letting_id}`}>
                  <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1 flex items-center gap-1"><Globe size={11} /> Live — click to view advert</p>
                  <div className="flex flex-wrap gap-1.5">
                    {l.portals.map((pk) => {
                      const meta = PORTALS.find((x) => x.key === pk);
                      const q = l.address || l.property_ref || "";
                      return (
                        <a key={pk} href={(PORTAL_URLS[pk] || (() => "#"))(q)} target="_blank" rel="noreferrer"
                          data-testid={`view-advert-${l.letting_id}-${pk}`} title={`View live advert on ${pk}`}
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 hover:opacity-80 transition-opacity ${meta ? meta.color : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                          {pk} <ExternalLink size={9} />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              <button data-testid={`publish-portals-${l.letting_id}`} onClick={() => openPortals(l)}
                className="mt-3 w-full border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-lg px-3 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors">
                <Globe size={13} /> {l.portals?.length ? "Manage portals" : "Send to portals"}
              </button>
              <button data-testid={`marketing-${l.letting_id}`} onClick={() => setMarketingFor(l)}
                className="mt-2 w-full border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors">
                <Megaphone size={13} /> Portal details
              </button>
              <div className="mt-2 flex items-center gap-2">
                <button data-testid={`zoopla-publish-${l.letting_id}`} onClick={() => publishZoopla(l)} disabled={zooplaBusy === l.letting_id}
                  className="flex-1 border border-indigo-200 text-indigo-700 hover:bg-indigo-50 rounded-lg px-3 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50">
                  {zooplaBusy === l.letting_id ? <Loader2 size={13} className="animate-spin" /> : <Radio size={13} />}
                  {l.zoopla_status === "published" ? "Update on Zoopla" : "Publish to Zoopla"}
                </button>
                {l.zoopla_status === "published" && (
                  <button data-testid={`zoopla-remove-${l.letting_id}`} onClick={() => removeZoopla(l)} disabled={zooplaBusy === l.letting_id}
                    className="border border-red-200 text-red-600 hover:bg-red-50 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50" title="Remove from Zoopla">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
              {l.zoopla_status === "published" && <p className="text-[10px] text-indigo-600 mt-1 text-center">● Live on Zoopla</p>}
              {(l.stage === "Offers Received" || l.stage === "Referencing") && (
                <button data-testid={`start-referencing-${l.letting_id}`} onClick={() => startReferencing(l)}
                  className={`mt-2 w-full rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${l.stage === "Referencing" ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}>
                  <ClipboardCheck size={13} /> {l.stage === "Referencing" ? "Manage referencing" : "Start Referencing"}
                </button>
              )}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Status</label>
                <select data-testid={`letting-status-${l.letting_id}`} value={l.stage} onChange={(e) => changeStage(l, e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                  {(stages.includes(l.stage) ? stages : [l.stage, ...stages]).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </Card>
          ))}
        </div>
      )}
      </>)}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Open New Letting</h3>
              <button type="button" data-testid="close-letting" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Property</label>
                <select data-testid="letting-property" value={form.property_id || ""} onChange={(e) => pickProperty(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                  <option value="">— Select a saved property —</option>
                  {props.map((pr) => (
                    <option key={pr.property_id} value={pr.property_id}>{pr.address}{pr.landlord ? ` · ${pr.landlord}` : ""}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">Property not here? <button type="button" onClick={() => navigate("/landlords")} className="text-blue-600 hover:underline">Add it under a landlord in Landlords</button> first.</p>
              </div>
              <div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Rent (£ pcm)</label>
                  <input data-testid="letting-rent" type="number" min="0" value={form.rent_pcm} onChange={(e) => setForm({ ...form, rent_pcm: e.target.value })} placeholder="1500" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <input data-testid="letting-address" value={form.address} readOnly placeholder="Auto-filled from the selected property" className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Applicant</label>
                  <input data-testid="letting-applicant" value={form.applicant_name} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select data-testid="letting-stage-select" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Target date</label>
                <input data-testid="letting-target-date" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea data-testid="letting-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <button type="submit" data-testid="save-letting" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Open Letting
              </button>
            </form>
          </div>
        </div>
      )}

      {mgOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setMgOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Lettings statuses</h3>
              <button type="button" data-testid="close-statuses" onClick={() => setMgOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="flex gap-2 mb-4">
              <input data-testid="new-status-input" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStatus(); } }}
                placeholder="Add a custom status…" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <button data-testid="add-status-button" onClick={addStatus} className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium">Add</button>
            </div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {stages.map((s) => {
                const isCustom = !defaults.includes(s);
                return (
                  <div key={s} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
                    <span className="text-sm text-slate-700">{s} {isCustom && <span className="text-[10px] text-blue-600 font-semibold ml-1">CUSTOM</span>}</span>
                    {isCustom && <button data-testid={`remove-status-${s}`} onClick={() => removeStatus(s)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {portalFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setPortalFor(null)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="portals-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-bold text-slate-900">Send to portals</h3>
              <button type="button" data-testid="close-portals" onClick={() => setPortalFor(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">{portalFor.address || portalFor.property_ref} — choose where this listing goes live.</p>
            <div className="space-y-2 mb-5">
              {PORTALS.map((p) => {
                const on = portalSel.includes(p.key);
                return (
                  <button key={p.key} type="button" data-testid={`portal-toggle-${p.key}`} onClick={() => togglePortal(p.key)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-colors ${on ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center border ${on ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>{on && <Check size={13} className="text-white" />}</span>
                    <span className="flex-1">
                      <span className="block text-sm font-semibold text-slate-800">{p.key}</span>
                      <span className="block text-xs text-slate-400">{p.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <button data-testid="save-portals" onClick={savePortals} disabled={portalBusy}
              className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {portalBusy ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />} {portalSel.length ? `Publish to ${portalSel.length} portal${portalSel.length > 1 ? "s" : ""}` : "Save (remove from all)"}
            </button>
          </div>
        </div>
      )}
      {marketingFor && (
        <MarketingModal kind="let" listing={marketingFor} onClose={() => setMarketingFor(null)}
          onSaved={() => { load(); refreshReadiness(); }} />
      )}
    </Layout>
  );
}

const Pill = ({ active, onClick, label, count, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium border transition-colors whitespace-nowrap ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
    {label} <span className={`ml-1 ${active ? "text-slate-300" : "text-slate-400"}`}>({count})</span>
  </button>
);
